import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./ImageWithBoxes.css";
import { getVisualEvidenceItems } from "../utils/explanationUtils";
import { getOptimizedImageUrl, getPublicAssetUrl } from "../utils/imagePaths";

const decodeCompressedRleCounts = (counts) => {
  const decoded = [];
  let position = 0;

  while (position < counts.length) {
    let value = 0;
    let shift = 0;
    let more = true;

    while (more && position < counts.length) {
      const charCode = counts.charCodeAt(position) - 48;
      value |= (charCode & 0x1f) << shift;
      more = Boolean(charCode & 0x20);
      shift += 5;
      position += 1;

      if (!more && (charCode & 0x10)) {
        value |= -1 << shift;
      }
    }

    if (decoded.length > 2) {
      value += decoded[decoded.length - 2];
    }
    decoded.push(value);
  }

  return decoded;
};

const decodeMaskRle = (maskRle) => {
  const size = maskRle?.size;
  const counts = maskRle?.counts;
  if (!Array.isArray(size) || size.length < 2 || !counts) {
    return null;
  }

  const [height, width] = size;
  const decodedCounts = typeof counts === "string" ? decodeCompressedRleCounts(counts) : counts;
  if (!Array.isArray(decodedCounts) || !Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }

  const pixels = new Uint8Array(width * height);
  let offset = 0;
  let value = 0;

  decodedCounts.forEach((count) => {
    const nextOffset = Math.min(offset + Math.max(0, count), pixels.length);
    if (value === 1) {
      for (let index = offset; index < nextOffset; index += 1) {
        const x = Math.floor(index / height);
        const y = index % height;
        pixels[y * width + x] = 1;
      }
    }
    offset = nextOffset;
    value = value ? 0 : 1;
  });

  return { width, height, pixels };
};

// EXIF orientations 5-8 swap width/height when the image is displayed.
const orientationSwapsAxes = (orientation) =>
  orientation === 5 || orientation === 6 || orientation === 7 || orientation === 8;

// Map a native (pre-EXIF-rotation) pixel coordinate to its displayed position.
// `w`/`h` are the native mask dimensions; the returned `dw`/`dh` are the
// displayed dimensions (axes swapped for orientations 5-8).
const mapPixelForOrientation = (x, y, w, h, orientation) => {
  switch (orientation) {
    case 2:
      return { dx: w - 1 - x, dy: y };
    case 3:
      return { dx: w - 1 - x, dy: h - 1 - y };
    case 4:
      return { dx: x, dy: h - 1 - y };
    case 5:
      return { dx: y, dy: x };
    case 6:
      return { dx: h - 1 - y, dy: x };
    case 7:
      return { dx: h - 1 - y, dy: w - 1 - x };
    case 8:
      return { dx: y, dy: w - 1 - x };
    default:
      return { dx: x, dy: y };
  }
};

// Decode an RLE mask into DISPLAY orientation (axes swapped for EXIF 5-8).
function decodeMaskToDisplay(maskRle, orientation = 1) {
  const decoded = decodeMaskRle(maskRle);
  if (!decoded) return null;
  const swap = orientationSwapsAxes(orientation);
  const width = swap ? decoded.height : decoded.width;
  const height = swap ? decoded.width : decoded.height;
  const pixels = new Uint8Array(width * height);
  for (let y = 0; y < decoded.height; y += 1) {
    for (let x = 0; x < decoded.width; x += 1) {
      if (!decoded.pixels[y * decoded.width + x]) continue;
      const { dx, dy } = mapPixelForOrientation(x, y, decoded.width, decoded.height, orientation);
      pixels[dy * width + dx] = 1;
    }
  }
  return { width, height, pixels };
}

// Normalized [0..1] bounding box of the masked region in display space.
function maskDisplayBBox(maskRle, orientation = 1) {
  const d = decodeMaskToDisplay(maskRle, orientation);
  if (!d) return null;
  let minX = Infinity, minY = Infinity, maxX = -1, maxY = -1;
  for (let y = 0; y < d.height; y += 1) {
    for (let x = 0; x < d.width; x += 1) {
      if (!d.pixels[y * d.width + x]) continue;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { bx: minX / d.width, by: minY / d.height, bw: (maxX - minX + 1) / d.width, bh: (maxY - minY + 1) / d.height };
}

// Zoom factor used to fill the card with the masked object.
function maskZoomScale(bbox, pad = 1.3, min = 1.05, max = 4.5) {
  if (!bbox) return 1;
  return Math.max(min, Math.min(max, 1 / (Math.max(bbox.bw, bbox.bh) * pad)));
}

// CSS transform that zooms the masked object to fill the card (with padding).
// Translate is clamped so the scaled image always fully covers the box — never
// revealing the grey letterbox background.
function maskZoomTransform(bbox) {
  if (!bbox) return "none";
  const s = maskZoomScale(bbox);
  const lo = 1 / s - 1; // <= 0
  const clamp = (t) => Math.max(lo, Math.min(0, t));
  const tx = clamp(0.5 / s - (bbox.bx + bbox.bw / 2)) * 100;
  const ty = clamp(0.5 / s - (bbox.by + bbox.bh / 2)) * 100;
  return `scale(${s.toFixed(3)}) translate(${tx.toFixed(2)}%, ${ty.toFixed(2)}%)`;
}

function EvidenceMaskOverlay({ maskRle, orientation = 1, zoomScale = 1 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const d = decodeMaskToDisplay(maskRle, orientation);
    const canvas = canvasRef.current;
    if (!d || !canvas) {
      return;
    }
    const W = d.width, H = d.height, P = d.pixels;
    canvas.width = W;
    canvas.height = H;
    const context = canvas.getContext("2d");
    const imageData = context.createImageData(W, H);
    const isSet = (x, y) => x >= 0 && y >= 0 && x < W && y < H && P[y * W + x] === 1;

    // edge pixels (a set pixel touching an unset one), dilated for a visible border
    const edge = new Uint8Array(W * H);
    for (let y = 0; y < H; y += 1) {
      for (let x = 0; x < W; x += 1) {
        if (P[y * W + x] && (!isSet(x - 1, y) || !isSet(x + 1, y) || !isSet(x, y - 1) || !isSet(x, y + 1))) {
          edge[y * W + x] = 1;
        }
      }
    }
    // Border thickness compensates for the default zoom so the outline stays a
    // consistent on-screen size instead of getting chunky when zoomed in hard.
    const r = Math.max(1, Math.round(Math.min(W, H) / 150 / Math.max(1, zoomScale)));
    const border = new Uint8Array(W * H);
    for (let y = 0; y < H; y += 1) {
      for (let x = 0; x < W; x += 1) {
        if (!edge[y * W + x]) continue;
        for (let dy = -r; dy <= r; dy += 1) {
          for (let dx = -r; dx <= r; dx += 1) {
            if (isSet(x + dx, y + dy)) border[(y + dy) * W + (x + dx)] = 1;
          }
        }
      }
    }
    for (let i = 0; i < W * H; i += 1) {
      if (!P[i]) continue;
      const o = i * 4;
      if (border[i]) {           // crisp opaque border
        imageData.data[o] = 22; imageData.data[o + 1] = 163; imageData.data[o + 2] = 74; imageData.data[o + 3] = 240;
      } else {                    // light, more transparent fill
        imageData.data[o] = 74; imageData.data[o + 1] = 222; imageData.data[o + 2] = 128; imageData.data[o + 3] = 56;
      }
    }
    context.clearRect(0, 0, W, H);
    context.putImageData(imageData, 0, 0);
  }, [maskRle, orientation, zoomScale]);

  if (!maskRle?.counts || !Array.isArray(maskRle?.size)) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
    />
  );
}

function EvidenceSvgMaskOverlay({ maskSvgPath, maskViewBox }) {
  if (!maskSvgPath || !Array.isArray(maskViewBox) || maskViewBox.length < 2) {
    return null;
  }

  const [width, height] = maskViewBox;
  if (![width, height].every(Number.isFinite) || width <= 0 || height <= 0) {
    return null;
  }

  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
    >
      <path
        d={maskSvgPath}
        fill="rgba(34, 197, 94, 0.34)"
        stroke="#16a34a"
        strokeWidth="10"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function evidenceAspectRatio(item) {
  const size = item?.maskRle?.size;
  if (Array.isArray(size) && size.length >= 2 && size[0] > 0 && size[1] > 0) {
    // size is [height, width] in the native (pre-rotation) space. For EXIF
    // orientations 5-8 the displayed image swaps axes, so swap here too.
    if (orientationSwapsAxes(item?.imageOrientation)) {
      return `${size[0]} / ${size[1]}`;
    }
    return `${size[1]} / ${size[0]}`;
  }
  const viewBox = item?.maskViewBox;
  if (Array.isArray(viewBox) && viewBox.length >= 2 && viewBox[0] > 0 && viewBox[1] > 0) {
    return `${viewBox[0]} / ${viewBox[1]}`;
  }
  return "4 / 3";
}

// One evidence image with its highlight overlay. The mask/segment overlay is
// only mounted once the image has loaded so it fades in with the photo instead
// of flashing on an empty box.
function EvidenceCard({ item, boundaryKey, imageRefs, eager, onEvidenceHover, onEvidenceClick }) {
  const [loaded, setLoaded] = useState(false);
  const bbox = useMemo(
    () => maskDisplayBBox(item.maskRle, item.imageOrientation),
    [item.maskRle, item.imageOrientation]
  );
  const zoom = maskZoomTransform(bbox);
  const zoomScale = maskZoomScale(bbox);
  const cap = item.hideCaption ? null : (item.conceptName || item.matchedConcept || "");
  const evidenceMeta = {
    boundaryKey,
    conceptName: item.conceptName || item.matchedConcept || null,
    imageUrl: item.imageUrl,
    kind: item.kind || null,
  };

  return (
    <figure
      ref={(node) => {
        if (node) imageRefs.current[boundaryKey] = node;
        else delete imageRefs.current[boundaryKey];
      }}
      className="evidence-card"
      style={{ contentVisibility: "auto", containIntrinsicSize: "260px 240px" }}
      onMouseEnter={onEvidenceHover ? () => onEvidenceHover(evidenceMeta) : undefined}
      onMouseLeave={onEvidenceHover ? () => onEvidenceHover(null) : undefined}
      onClick={onEvidenceClick ? () => onEvidenceClick(evidenceMeta) : undefined}
    >
      <div
        className={`evidence-card__media${zoom !== "none" ? " evidence-card__media--zoomable" : ""}${loaded ? " is-loaded" : ""}`}
        style={{ aspectRatio: evidenceAspectRatio(item) }}
        title={zoom !== "none" ? "Hover to see the full photo" : undefined}
      >
        <div className="evidence-card__zoom" style={{ "--zt": zoom }}>
          <img
            src={getOptimizedImageUrl(item.imageUrl)}
            alt={item.label || item.kind || "Visual evidence"}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={eager ? "high" : "low"}
            className="evidence-card__image"
            onLoad={(event) => {
              // Match the box to the image's true aspect (no grey letterbox, aligned mask).
              const nw = event.currentTarget.naturalWidth;
              const nh = event.currentTarget.naturalHeight;
              const media = event.currentTarget.closest(".evidence-card__media");
              if (media && nw && nh) media.style.aspectRatio = `${nw} / ${nh}`;
              setLoaded(true);
            }}
            onError={(event) => {
              if (event.currentTarget.dataset.fallbackApplied) {
                setLoaded(true);
                return;
              }
              event.currentTarget.dataset.fallbackApplied = "true";
              event.currentTarget.src = getPublicAssetUrl(item.imageUrl);
            }}
          />
          {loaded && <EvidenceMaskOverlay maskRle={item.maskRle} orientation={item.imageOrientation} zoomScale={zoomScale} />}
          {loaded && <EvidenceSvgMaskOverlay maskSvgPath={item.maskSvgPath} maskViewBox={item.maskViewBox} />}
        </div>
      </div>
      {cap ? <figcaption className="evidence-card__cap">{cap}</figcaption> : null}
    </figure>
  );
}

/**
 * Renders visual and/or textual explanation reasoning entries for the hovered segment.
 *
 * Props:
 *   visualExplanations   — array of visualExplanation entries
 *   textualExplanations  — optional array of factsExplanation entries
 *   showVisualReasoning  — whether to show reasoning text from visualExplanation
 *   hoveredId            — currently hovered segment id
 */
const VisualTextualExplanation = forwardRef(
  ({
    visualExplanations,
    textualExplanations,
    showVisualReasoning = true,
    hoveredId,
    requireVisualEvidence = false,
    onEvidenceHover,
    onEvidenceClick,
  }, ref) => {
    const containerRef = useRef(null);
    const headerRef = useRef(null);
    const imageRefs = useRef({});

    const visualEntriesWithEvidence = useMemo(
      () =>
        (visualExplanations || [])
          .map((entry, entryIndex) => ({
            entry,
            entryIndex,
            evidenceItems: getVisualEvidenceItems(entry),
          }))
          .filter((item) => !requireVisualEvidence || item.evidenceItems.length > 0),
      [visualExplanations, requireVisualEvidence]
    );

    const visualEvidenceCount = visualEntriesWithEvidence.reduce(
      (count, item) => count + item.evidenceItems.length,
      0
    );

    useImperativeHandle(
      ref,
      () => ({
        getBoundingClientRect: () =>
          containerRef.current?.getBoundingClientRect(),
        getSubComponentBoundaries: () => {
          const boundaries = {};
          if (headerRef.current) {
            const rect = headerRef.current.getBoundingClientRect();
            boundaries["visualTextualExplanation_header"] = {
              x: rect.x, y: rect.y, width: rect.width, height: rect.height,
              top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom,
            };
          }
          Object.entries(imageRefs.current).forEach(([key, node]) => {
            if (!node) return;
            const rect = node.getBoundingClientRect();
            boundaries[key] = {
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
              top: rect.top,
              left: rect.left,
              right: rect.right,
              bottom: rect.bottom,
            };
          });
          return boundaries;
        },
      }),
      []
    );

    useEffect(() => {
      const t1 = setTimeout(() => window.updateBoundaries?.(), 0);
      const t2 = setTimeout(() => window.updateBoundaries?.(), 150);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [visualExplanations?.length, textualExplanations?.length, visualEvidenceCount]);

    const visEntries = visualEntriesWithEvidence;
    const txtEntries = textualExplanations || [];
    const hasVisual = visEntries.length > 0;
    const hasTextual = txtEntries.length > 0;

    const renderEvidenceGallery = (evidenceItems, entryIdx) => {
      if (!evidenceItems.length) {
        return null;
      }

      // The "matched region" wording only fits masked evidence (the method's
      // concept grounding). Location/identity/crop items are plain reference
      // photos with no highlighted region, so label them accordingly.
      const hasMask = evidenceItems.some(
        (it) => (it.maskRle && it.maskRle.counts) || it.maskSvgPath
      );
      const kinds = new Set(evidenceItems.map((it) => it.kind));
      const allLocation = [...kinds].every((k) => k === "location-reference");
      const allPeople = [...kinds].every(
        (k) => k === "identity-gallery" || k === "current-image-crop"
      );
      let icon = "🔍";
      let labelText = "Visual evidence the AI matched";
      let hint = "(highlighted region shows what it looked at)";
      if (!hasMask) {
        if (allLocation) {
          icon = "📍";
          labelText = "Reference images of this place";
          hint = "";
        } else if (allPeople) {
          icon = "👤";
          labelText = "Other photos of this person";
          hint = "";
        } else {
          icon = "📷";
          labelText = "Reference images the AI compared with";
          hint = "";
        }
      }

      return (
        <div className="evidence-block">
          <div className="evidence-label">
            <span className="evidence-label__icon" aria-hidden="true">{icon}</span>
            {labelText}
            {hint && <span className="evidence-label__hint">{hint}</span>}
          </div>
          <div className="evidence-gallery">
            {evidenceItems.map((item, imageIdx) => (
              <EvidenceCard
                key={`visualTextualExplanation_image_${entryIdx}_${imageIdx}`}
                item={item}
                boundaryKey={`visualTextualExplanation_image_${entryIdx}_${imageIdx}`}
                imageRefs={imageRefs}
                eager={imageIdx === 0}
                onEvidenceHover={onEvidenceHover}
                onEvidenceClick={onEvidenceClick}
              />
            ))}
          </div>
        </div>
      );
    };

    const renderEntries = (entries, label) => (
      <>
        {label && <h3 className="explanation-section-title">{label}</h3>}
        {entries.map((entryData, idx) => {
          const entry = entryData?.entry || entryData;
          const evidenceItems = entryData?.evidenceItems || [];
          const hasReasoning = Boolean(entry.reasoning) && showVisualReasoning;

          return (
            <article key={idx} className={`explanation-card explanation-card--visual${entry.cross_reference ? " explanation-card--cross-ref" : ""}`}>
              <div className="explanation-card__header">
                <span className="explanation-card__target">{entry.text}</span>
                {entry.cross_reference && (
                  <span className="explanation-chip explanation-chip--cross-ref" title={entry.cross_reference_label || "Cross-reference"}>
                    Also seen earlier
                  </span>
                )}
                {evidenceItems.length > 0 && (
                  <span className="explanation-chip explanation-chip--evidence">
                    {evidenceItems.length} {evidenceItems.length === 1 ? "image" : "images"}
                  </span>
                )}
              </div>
              {hasReasoning ? (
                <p className="explanation-card__reasoning">
                  {entry.reasoning}
                </p>
              ) : evidenceItems.length > 0 ? (
                <p className="explanation-card__hint">
                  Image evidence for this caption part.
                </p>
              ) : (
                <p className="explanation-card__hint explanation-card__hint--muted">
                  No reasoning text or visual evidence provided.
                </p>
              )}
              {renderEvidenceGallery(evidenceItems, entryData?.entryIndex ?? idx)}
            </article>
          );
        })}
      </>
    );

    return (
      <div ref={containerRef} className="explanation-panel">
        <div ref={headerRef} className="explanation-panel__header">
          <h2 className="explanation-panel__title">Why This Caption Part Was Highlighted</h2>
          {(hasVisual || hasTextual) && (
            <span className="explanation-panel__count">
              {visEntries.length + txtEntries.length} {visEntries.length + txtEntries.length === 1 ? "item" : "items"}
            </span>
          )}
        </div>
        <div className="visual-scroll-container explanation-panel__body">
          {!hasVisual && !hasTextual && (
            <div className="explanation-empty-state">
              <div className="explanation-empty-state__icon" aria-hidden="true">👆</div>
              <div className="explanation-empty-state__title">See why the AI wrote each part</div>
              <div className="explanation-empty-state__text">
                Tap or hover an <span className="ee-underline">underlined caption part</span> (or a region on the photo)
                to reveal the AI's reasoning and the visual evidence it used. Click to keep it open.
              </div>
            </div>
          )}
          {hasVisual && renderEntries(visEntries, hasTextual ? "Visual Reasoning" : null)}
          {hasTextual && renderEntries(txtEntries, hasVisual ? "Textual Reasoning" : null)}
        </div>
      </div>
    );
  }
);

VisualTextualExplanation.displayName = "VisualTextualExplanation";

export default VisualTextualExplanation;
